import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useAppMessages } from '@components/appMessages/AppMessagesProvider.tsx';

/**
 * Planned maintenance / incident notice.
 *
 * Rendered as the first child of a layout's full-height flex column, so it takes real layout space
 * and the shell below — menu included — starts under it. That push is the requirement: the notice
 * runs for the whole announced period and must never sit on top of anything the user is working on.
 * It carries no controls and cannot be dismissed.
 *
 * It stays inside the layout rather than moving up to _app.tsx because the layouts are h-screen and
 * lock document scroll; a banner added above them would push a 100vh shell past the viewport.
 */
const MaintenanceBanner: React.FC = () => {
  const { alerts } = useAppMessages();
  const ref = useRef<HTMLDivElement>(null);

  // The version bar is a fixed overlay; it reads this to sit below the strip instead of over it.
  const publishHeight = useCallback(() => {
    const h = ref.current?.getBoundingClientRect().height ?? 0;
    document.documentElement.style.setProperty('--app-maintenance-height', `${Math.round(h)}px`);
  }, []);

  useLayoutEffect(publishHeight);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--app-maintenance-height', '0px');
    };
  }, [publishHeight, alerts.length]);

  if (alerts.length === 0) return null;

  return (
    <div ref={ref} className={'w-full shrink-0 flex flex-col justify-center gap-1 px-5 py-2 bg-[#ff9800]'}>
      {alerts.map(a => (
        <div key={a.id} className={'text-[#212121]'}>
          {a.text}
        </div>
      ))}
    </div>
  );
};

export default MaintenanceBanner;
